// 2026-11-03 - FormuLearning - Step 1 식약처 의약품 특허정보 표 (이슈 #105, M1002-21)
// 파일 위치: frontend/src/features/step1/MfdsPatentSection.tsx
//
// KIPRIS 표(`PatentSection`)와 **합치지 않고 그 아래 별도 표**로 둔다 — *"KIPRIS와 식약처를 합치지 마라. 별도 표로.
// 헤더 가급적 맞춤."*(261002 회의). 헤더 순서는 KIPRIS 와 같게 `Type · (날짜) · Holder · Title` 이고, 이 API 에는
// 출원일이 없어 날짜 자리는 존속기간 만료일(Expiry Date)이다. 상태(Status)는 끝에 덧붙인다.
// 행 = 특허 1건(백엔드가 «품목 × 특허» 응답을 묶음). 기본은 **제품특허**(품목에 등재된 특허)만 —
// «기타특허»·«미등록 출원»은 칩을 켜면 보인다(장지연 교수 261103 결정, 취소·만료 품목과 같은 «기본 숨김, 켜면 표시»).

import { Fragment, useState } from 'react'
import type { MfdsPatent, MfdsPatents } from '../../api/step1'
import Collecting from './Collecting'

interface Props {
  data: MfdsPatents | null
  loading?: boolean
}

const PRODUCT = '제품특허'

type Group = 'product' | 'other' | 'unregistered'

/** 표시 묶음 — 제품특허 / 기타특허(번호 있음: 등록·소멸) / 미등록 출원(번호 없음: 거절·취하·심사중 …) */
function groupOf(p: MfdsPatent): Group {
  if (p.page_gb === PRODUCT) return 'product'
  return p.registered ? 'other' : 'unregistered'
}

const GROUP_LABEL: Record<Exclude<Group, 'product'>, string> = {
  other: '기타특허',
  unregistered: '미등록 출원',
}

type SortKey = 'type' | 'expiry_date' | 'holder' | 'title' | 'status'

/** `10-1432821` → Google Patents `KR101432821` 딥링크. 번호가 없으면 제목 검색 */
function googlePatentUrl(no: string | null, title: string | null): string | null {
  const core = (no ?? '').replace(/\D/g, '')
  if (core.length === 8 || core.length === 9) return `https://patents.google.com/patent/KR${core}`
  return title ? `https://patents.google.com/?q=${encodeURIComponent(`(TI="${title}")`)}` : null
}

export default function MfdsPatentSection({ data, loading }: Props) {
  const [show, setShow] = useState<Record<Exclude<Group, 'product'>, boolean>>({ other: false, unregistered: false })
  const [expanded, setExpanded] = useState<number | null>(null)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)

  const patents = data?.patents ?? []
  const meta = data?.meta
  const counts = { product: 0, other: 0, unregistered: 0 }
  for (const p of patents) counts[groupOf(p)] += 1

  let view = patents.map((p, i) => ({ p, i })).filter(({ p }) => {
    const g = groupOf(p)
    return g === 'product' || show[g]
  })
  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1
    view = [...view].sort((a, b) => {
      const va = String(a.p[sort.key] ?? '').toLowerCase()
      const vb = String(b.p[sort.key] ?? '').toLowerCase()
      if (va === vb) return 0
      if (va === '') return 1 // 빈 값은 방향과 무관하게 뒤로
      if (vb === '') return -1
      return va < vb ? -dir : dir
    })
  }

  const toggleSort = (key: SortKey) =>
    setSort((cur) => (cur?.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null))
  const sortMark = (key: SortKey) => (sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  const countLabel = meta && meta.patent_count > 0
    ? `식약처 '${meta.query}' 품목×특허 ${meta.rows.toLocaleString()}행 → 특허 ${meta.patent_count}건 · ${view.length}건 표시`
    : null

  const th = 'cursor-pointer select-none px-3 py-1.5 font-medium text-text-secondary hover:text-text-body'

  return (
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Patent Information (MFDS)</h4>
        {countLabel && <span className="text-[10px] text-text-muted">{countLabel}</span>}
      </div>

      {patents.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium"
                style={{ borderColor: 'var(--color-accent-purple)', background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}
                title="품목 허가 시 등재된 특허 — 항상 표시">
            {PRODUCT} {counts.product}
          </span>
          {(Object.keys(GROUP_LABEL) as Exclude<Group, 'product'>[]).map((g) => (
            counts[g] > 0 && (
              <button key={g} type="button" onClick={() => setShow((s) => ({ ...s, [g]: !s[g] }))}
                      className="rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors"
                      title={g === 'other' ? '품목에 등재되지 않은 관련 특허(등록·소멸) — 켜면 표시' : '등록되지 않은 출원(거절·취하·심사중 등, 특허번호 없음) — 켜면 표시'}
                      style={{
                        borderColor: show[g] ? 'var(--color-accent-purple)' : 'var(--border-subtle)',
                        background: show[g] ? 'var(--bg-chip-purple)' : 'transparent',
                        color: show[g] ? 'var(--color-accent-purple)' : 'var(--text-muted)',
                      }}>
                {GROUP_LABEL[g]} {counts[g]}
              </button>
            )
          ))}
        </div>
      )}

      {!loading && meta?.fetch_failed && (
        <p className="mb-2 text-[11px]" style={{ color: 'var(--color-warn)' }}>
          ⚠ 식약처 특허정보 조회 실패 — 공공데이터포털이 응답하지 않았습니다({meta.failed_queries.join(', ')}). 재검색하면 다시 조회합니다.
        </p>
      )}

      {patents.length === 0 ? (
        loading ? <Collecting />
          : meta?.fetch_failed ? null
          : <p className="text-[11px] text-text-muted">{data ? '식약처 특허정보가 없습니다.' : '이 검색에는 식약처 특허정보가 없습니다 — 재검색하면 조회합니다.'}</p>
      ) : view.length === 0 ? (
        <p className="text-[11px] text-text-muted">제품특허가 없습니다 — 위 칩을 켜면 기타특허·미등록 출원을 볼 수 있습니다.</p>
      ) : (
        <div className="panel-in overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th onClick={() => toggleSort('type')} className={`${th} whitespace-nowrap text-left`}>Type{sortMark('type')}</th>
                <th onClick={() => toggleSort('expiry_date')} className={`${th} whitespace-nowrap text-center`}>Expiry Date{sortMark('expiry_date')}</th>
                <th onClick={() => toggleSort('holder')} className={`${th} text-left`}>Holder{sortMark('holder')}</th>
                <th onClick={() => toggleSort('title')} className={`${th} text-left`}>Title{sortMark('title')}</th>
                <th onClick={() => toggleSort('status')} className={`${th} whitespace-nowrap text-left`}>Status{sortMark('status')}</th>
                <th className="px-3 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {view.map(({ p, i }) => {
                const open = expanded === i
                const g = groupOf(p)
                const link = googlePatentUrl(p.patent_no, p.title)
                return (
                  <Fragment key={i}>
                    <tr onClick={() => setExpanded(open ? null : i)} className="cursor-pointer border-t"
                        style={{ borderColor: 'var(--border-subtle)', backgroundColor: open ? 'var(--bg-row-active)' : 'transparent' }}>
                      <td className="px-3 py-1.5">
                        {p.type && (
                          <span className="inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium"
                                style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>{p.type}</span>
                        )}
                        {g !== 'product' && <span className="ml-1 text-[9px] text-text-muted">{GROUP_LABEL[g]}</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-center text-text-body">{p.expiry_date}</td>
                      <td className="max-w-[145px] truncate px-3 py-1.5 text-text-muted">{p.holder}</td>
                      <td className="px-3 py-1.5 text-text-muted">{p.title}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-text-muted">{p.status}</td>
                      <td className="px-3 py-1.5 text-right text-[9px] text-text-secondary">{open ? '▲' : '▼'}</td>
                    </tr>
                    {open && (
                      <tr style={{ backgroundColor: 'var(--bg-row-active)' }}>
                        <td colSpan={6} className="px-3 py-2">
                          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                            <dt className="whitespace-nowrap font-medium text-text-secondary">Patent No.</dt>
                            <dd className="text-text-body">{p.patent_no ?? '— (미등록 출원)'}</dd>
                            <dt className="whitespace-nowrap font-medium text-text-secondary">Listing</dt>
                            <dd className="text-text-body">{p.page_gb}</dd>
                            <dt className="whitespace-nowrap font-medium text-text-secondary">Products ({p.product_count})</dt>
                            <dd className="text-text-body">
                              <ul className="space-y-0.5">
                                {p.products.map((it) => (
                                  <li key={it.item_seq}>
                                    {it.item_name}
                                    <span className="text-text-muted"> · {it.maker}{it.pms_end_date ? ` · 재심사 ${it.pms_end_date}` : ''}</span>
                                  </li>
                                ))}
                              </ul>
                            </dd>
                          </dl>
                          {link && (
                            <a href={link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                               className="mt-2 inline-block text-[10px] font-medium underline" style={{ color: 'var(--color-accent-purple)' }}>
                              Google Patents ↗
                            </a>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {patents.length > 0 && (
        <p className="mt-2 px-1 text-[10px] leading-relaxed text-text-muted">
          출처: 식약처 의약품 특허정보(공공데이터포털). 실제 특허 침해 여부는 전문가 확인이 필요합니다.
        </p>
      )}
    </div>
  )
}
